
/**
 * Product form validation and data processing utilities
 */

export interface ProductFormData {
  name: string;
  description?: string;
  short_description?: string;
  price: number;
  compare_at_price?: number;
  category_id?: string;
  stock_quantity: number;
  sku?: string;
  status: string;
  featured: boolean;
  weight?: number;
  image_url?: string;
  tags?: string;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  url_slug?: string;
  seo_enabled?: boolean;
}

/**
 * Validates product form data before submission
 */
export const validateProductData = (data: ProductFormData): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required field validation
  if (!data.name || data.name.trim().length === 0) {
    errors.push('Product name is required');
  }

  if (data.price === undefined || data.price < 0) {
    errors.push('Valid price is required');
  }

  if (data.stock_quantity === undefined || data.stock_quantity < 0) {
    errors.push('Valid stock quantity is required');
  }

  // SEO validation when enabled
  if (data.seo_enabled) {
    if (data.url_slug && !/^[a-z0-9-]+$/.test(data.url_slug)) {
      errors.push('URL slug can only contain lowercase letters, numbers, and hyphens');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Sanitizes and prepares product data for database insertion
 */
export const sanitizeProductData = (data: ProductFormData, galleryImages: string[] = []): any => {
  const sanitized: any = {
    name: data.name.trim(),
    description: data.description?.trim() || null,
    short_description: data.short_description?.trim() || null,
    price: Number(data.price),
    compare_at_price: data.compare_at_price ? Number(data.compare_at_price) : null,
    category_id: data.category_id === "none" ? null : data.category_id,
    stock_quantity: Number(data.stock_quantity),
    sku: data.sku?.trim() || null,
    status: data.status,
    featured: Boolean(data.featured),
    weight: data.weight ? Number(data.weight) : null,
    image_url: data.image_url?.trim() || null,
    tags: data.tags ? data.tags.split(",").map(tag => tag.trim()).filter(Boolean) : null,
    gallery_images: galleryImages.length > 0 ? galleryImages : null,
  };

  // Handle SEO fields conditionally
  if (data.seo_enabled) {
    sanitized.seo_enabled = true;
    sanitized.seo_title = data.seo_title?.trim() || null;
    sanitized.seo_description = data.seo_description?.trim() || null;
    sanitized.seo_keywords = data.seo_keywords?.trim() || null;
    sanitized.url_slug = data.url_slug?.trim() || null;
  } else {
    sanitized.seo_enabled = false;
    sanitized.seo_title = null;
    sanitized.seo_description = null;
    sanitized.seo_keywords = null;
    sanitized.url_slug = null;
  }

  return sanitized;
};

/**
 * Generates a URL slug from product name
 */
export const generateUrlSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Validates URL slug format
 */
export const isValidUrlSlug = (slug: string): boolean => {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
};
